import _ from 'lodash'
import moment from 'moment'

// Utils
import commonsApi from '@/api/commons'

// Mixins
import lmToasts from '@/mixins/lmToasts'

export default {
  mixins: [lmToasts],
  props: {
    isOnModal: {
      type: Boolean,
      default: false
    },
    frontendRouteToRedirectAfterCreated: {
      type: String
    },
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      fieldToFocus: null,
      isLoading: false,
      originalForm: {},
      actionsAvailables: {
        canBeCreatedByMe: false,
        canBeEditedByMe: false,
        canBeDeletedByMe: false
      },
      hasChanged: false,
      isProcessing: true
    }
  },

  computed: {
    iCanDoIt () {
      return this.actionsAvailables.canBeCreatedByMe || this.actionsAvailables.canBeEditedByMe
    }
  },

  watch: {
    form: {
      deep: true,
      handler (newVal) {
        if ((this.isOnEditMode && this.isLoading) || this.isOnModal) { return }

        // Función para comparar profundamente y ignorar ciertas propiedades
        const deepCompare = (obj1, obj2) => {
          const ignoreKeys = ['_id', 'id', 'createdAt', 'updatedAt']
          return _.isEqual(
            _.omit(obj1, ignoreKeys),
            _.omit(obj2, ignoreKeys)
          )
        }

        // Verifica si hay cambios significativos entre el nuevo y el viejo valor
        if (!deepCompare(newVal, this.originalForm)) {
          this.hasChanged = true
        } else {
          this.hasChanged = false
        }
      }
    }
  },

  methods: {
    v (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    },
    async getActionsAvailables () {
      const targetEndpoint = `/${this.serviceEndpoint}/actionsavailable/`
      const response = await commonsApi.axiosGetWrapper(targetEndpoint)
      this.actionsAvailables = response.data
    },
    async onSubmit (finishWithGoBack = true) {
      try {
        await this.validateForm(this.$v)
        this.isProcessing = true
        this.form.issueDate = moment(this.form.issueDate, this.dateFormat).toDate()

        let res
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`, this.form)
        } else {
          res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}`, this.form)
        }
        this.hasChanged = false
        this.lmToastSuccessMessage(
          `${this.$t('saved_successfully')} '${this.form.name}'`,
          `${this.$t('action_done_successfully')}`
        )

        if (this.isOnModal) {
          this.$emit('itemCreated', {
            ...res.data,
            id: res.data._id
          })
          this.onClose()
        } else {
          if (finishWithGoBack) {
            this.onGoBack()
          } else if (!this.isOnEditMode) {
            if (this.frontendRouteToRedirectAfterCreated) {
              this.$router.replace(`${this.frontendRouteToRedirectAfterCreated}${res.data._id}`)
            } else {
              this.onGoBack()
            }
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
        this.hasChanged = false
      }
    },
    async onDelete () {
      try {
        this.isProcessing = true
        await commonsApi.axiosDeleteWrapper(`/${this.serviceEndpoint}/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(
          `${this.$t('was_successfully_deleted')} ${this.form.name}`,
          `${this.$t('action_done_successfully')}`
        )
        this.onGoBack()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    onClose () {
      this.$emit('closeModal')
    },
    async onReactivate () {
      try {
        this.isProcessing = true
        await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/reactivate/${this.$route?.params?.id}`)
        this.lmToastSuccessMessage(`${this.$t('was_successfully_reactivated')}`, `${this.$t('it_was_successfully_reactivated')}`)
        this.onGoBack()
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`)
      this.form = res.data
    },
    forceFocus () {
      if (this.fieldToFocus) {
        this.$nextTick(() => {
          this.$refs[this.fieldToFocus].forceFocus()
        })
      }
    },
    onGoBack () {
      if (this.hasChanged) {
        this.showUnsavedChangesModal({ path: this.$router.currentRoute.path }, () => {
          this.$router.go(-1) // Navega hacia atrás después de la confirmación
        })
      } else {
        this.$router.go(-1) // Navega hacia atrás directamente si no hay cambios
      }
    },
    async handleContinue (to, next) {
      this.hasChanged = false
      next() // Continúa la navegación
    },
    async handleSave (to, next) {
      await this.onSubmit(false) // Llama al método para guardar los cambios
      next() // Continúa la navegación
    },
    showUnsavedChangesModal (to, next) {
      this.lmModalWarningMessage({
        resultTitle: this.$t('changes_unsaved'),
        resultSubtitle: this.$t('changes_unsaved_subtitle'),
        buttons: [
          {
            btnText: this.$t('continue'),
            btnCallback: () => this.handleContinue(to, next)
          },
          {
            btnText: this.$t('save'),
            btnCallback: () => this.handleSave(to, next)
          }
        ]
      })
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.hasChanged) {
      this.showUnsavedChangesModal(to, next)
      return false
    }
    next()
  },
  async created () {
    try {
      this.isLoading = true
      await this.getActionsAvailables()
      if (this.isOnEditMode) {
        await this.loadData()
      }
      this.isProcessing = false
    } catch (error) {
      console.log(error)
    } finally {
      this.originalForm = _.cloneDeep(this.form)
      this.isLoading = false
      this.forceFocus()
    }
  }
}
